import React from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, PhoneIcon, SunIcon, MoonIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Disclosure } from '@headlessui/react';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage, Language } from '../../contexts/LanguageContext';
import useLeads from '../../hooks/useLeads';
import LeadForm from '../ui/LeadForm';


const Header = () => {
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage, t } = useLanguage();
  const {
    isLeadFormOpen,
    leadFormData,
    closeLeadForm,
    requestCallback
  } = useLeads();
  
  const navigation = [
    { name: t('nav.home'), href: '/' },
    { name: t('nav.about'), href: '/about' },
    { name: t('nav.services'), href: '/services' },
    { name: t('nav.certificates'), href: '/certificates' },
    { name: t('nav.contacts'), href: '/contacts' },
  ];
  
  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
  };
  return (
    <>
      {/* Top Bar */}
      <div className="bg-secondary-800 dark:bg-secondary-900 text-white text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-10">
            <div className="flex items-center space-x-3 md:space-x-6">
              <a href="https://wa.me/77089071712" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-[#25D366] transition-colors">
                <svg viewBox="0 0 32 32" fill="currentColor" className="h-4 w-4 mr-1 md:mr-2 text-[#25D366]">
                  <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.118-1.958A15.924 15.924 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.318 22.614c-.39 1.1-1.932 2.014-3.17 2.28-.846.18-1.95.324-5.668-1.218-4.762-1.974-7.826-6.81-8.064-7.126-.228-.316-1.926-2.566-1.926-4.892s1.218-3.47 1.652-3.944c.434-.474.948-.592 1.264-.592.316 0 .632.002.908.016.292.016.682-.11 1.066.814.39.948 1.328 3.234 1.444 3.47.118.236.196.512.04.828-.158.316-.236.512-.474.79-.236.276-.498.616-.71.826-.238.236-.486.492-.208.966.276.474 1.228 2.028 2.638 3.286 1.812 1.618 3.34 2.12 3.814 2.358.474.236.75.198 1.028-.118.276-.316 1.194-1.388 1.512-1.864.316-.474.632-.394 1.066-.236.434.158 2.718 1.282 3.184 1.516.466.236.776.354.892.55.118.196.118 1.136-.272 2.236z"/>
                </svg>
                <div className="flex flex-col">
                  <span className="text-xs text-secondary-400 leading-none">WhatsApp</span>
                  <span className="hidden sm:inline text-sm font-medium">+7 (708) 907-1712</span>
                  <span className="sm:hidden text-xs font-medium">+7 708 907-1712</span>
                </div>
              </a>
              <span className="text-secondary-500">|</span>
              <a href="tel:+77084931267" className="flex items-center hover:text-accent-orange transition-colors">
                <PhoneIcon className="h-4 w-4 mr-1 md:mr-2" />
                <div className="flex flex-col">
                  <span className="text-xs text-secondary-400 leading-none">{t('header.phone')}</span>
                  <span className="hidden sm:inline text-sm font-medium">+7 (708) 493-1267</span>
                  <span className="sm:hidden text-xs font-medium">+7 708 493-1267</span>
                </div>
              </a>
              <span className="text-secondary-500">|</span>
              <a href="https://www.instagram.com/kazpolimerplast?igsh=OHlpZWc0eHhleGo2" target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-pink-500 transition-colors">
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-1 text-pink-500">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
                <span className="hidden md:inline text-sm">Instagram</span>
              </a>
              <span className="hidden md:inline text-secondary-500">|</span>
              <span className="hidden md:inline">{t('header.address')}</span>
              <span className="md:hidden">{t('header.addressShort')}</span>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <span className="hidden sm:inline">{t('header.workHours')}</span>
              <span className="sm:hidden text-xs">9:00-18:00</span>
              <div className="flex items-center space-x-2 md:space-x-4">
                <div className="flex space-x-2">
                  <button 
                    onClick={() => handleLanguageChange('ru')}
                    className={`text-sm transition-colors ${
                      language === 'ru' ? 'text-accent-orange' : 'hover:text-accent-orange'
                    }`}
                  >
                    RU
                  </button>
                  <span>|</span>
                  <button 
                    onClick={() => handleLanguageChange('kz')}
                    className={`text-sm transition-colors ${
                      language === 'kz' ? 'text-accent-orange' : 'hover:text-accent-orange'
                    }`}
                  >
                    KZ
                  </button>
                </div>
                <button
                  onClick={toggleTheme}
                  className="p-1 rounded-full hover:bg-secondary-700 dark:hover:bg-secondary-800 transition-colors"
                >
                  {theme === 'light' ? (
                    <MoonIcon className="h-4 w-4" />
                  ) : (
                    <SunIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <Disclosure as="nav" className="bg-white dark:bg-secondary-900 shadow-sm border-b border-secondary-200 dark:border-secondary-700">
        {({ open }) => (
          <>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-20">
                <div className="flex items-center">
                  <Link to="/" className="flex-shrink-0 flex items-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg flex items-center justify-center mr-2 md:mr-3 p-1 shadow-sm">
                      <img 
                        src="/images/kazpolimerplast-logo.png" 
                        alt="KazPolimerPlast Logo" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-secondary-800 dark:text-white truncate">
                        <span className="hidden sm:inline">{t('hero.company')}</span>
                        <span className="sm:hidden">KazPolimerPlast</span>
                      </h1>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 hidden sm:block">{t('hero.tagline')}</p>
                    </div>
                  </Link>
                </div>
                
                <div className="hidden lg:flex lg:items-center lg:space-x-6">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="text-secondary-700 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>

                <div className="hidden lg:flex lg:items-center lg:space-x-4">
                  <Link
                    to="/pricelist"
                    className="p-2 text-secondary-600 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center"
                  >
                    <DocumentTextIcon className="h-6 w-6 mr-1" />
                    <span className="text-sm">{t('header.viewPrice')}</span>
                  </Link>
                  <button 
                    onClick={requestCallback}
                    className="bg-accent-orange text-white px-4 xl:px-6 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors duration-200 whitespace-nowrap"
                  >
                    <span className="hidden xl:inline">{t('nav.orderCall')}</span>
                    <span className="xl:hidden">{t('nav.orderCallShort')}</span>
                  </button>
                </div>

                <div className="lg:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center p-2 rounded-md text-secondary-600 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-secondary-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                    <span className="sr-only">{t('header.openMenu')}</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="lg:hidden">
              <div className="pt-2 pb-3 space-y-1 bg-white dark:bg-secondary-900 border-t border-secondary-200 dark:border-secondary-700">
                {navigation.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className="block pl-3 pr-4 py-2 border-l-4 text-base font-medium border-transparent text-secondary-700 dark:text-secondary-300 hover:bg-gray-50 dark:hover:bg-secondary-800 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {item.name}
                  </Disclosure.Button>
                ))}
              </div>
              <div className="pt-4 pb-3 border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-800">
                <div className="flex items-center px-4 space-x-3">
                  <Link
                    to="/pricelist"
                    className="flex-shrink-0 p-2 text-secondary-600 dark:text-secondary-300 hover:text-primary-600 dark:hover:text-primary-400 flex items-center"
                  >
                    <DocumentTextIcon className="h-6 w-6 mr-1" />
                    <span className="text-sm">{t('header.viewPrice')}</span>
                  </Link>
                  <button 
                    onClick={requestCallback}
                    className="bg-accent-orange text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 flex-1"
                  >
                    {t('nav.orderCall')}
                  </button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Lead Form */}
      <LeadForm
        isOpen={isLeadFormOpen}
        onClose={closeLeadForm}
        leadType={leadFormData.leadType}
        productName={leadFormData.productName}
        productId={leadFormData.productId}
        calculatedPrice={leadFormData.calculatedPrice}
      />
    </>
  );
};

export default Header;